import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { useLoading } from '../providers/LoadingContext';
import usePostFrameHeight from '../hooks/usePostFrameHeight';
import orderApi from '../api/order';
import { getTenantLinks } from '../tenants/runtimeConfig';

import '../assets/scss/order-page.scss';

// The parent storefront origin, resolved once. For a cross-origin iframe
// `document.referrer` gives at least the parent's origin, so the Need-help
// anchors can carry an honest href (the real storefront URL shows in the browser
// status bar on hover) instead of `#` — which would misleadingly show the
// iframe's own URL. An empty/blocked referrer (direct load, strict
// referrer-policy) leaves this null and the href falls back to the bare path.
const parentOrigin = (() => {
  try {
    return new URL(document.referrer).origin;
  } catch {
    return null;
  }
})();

// Prefix a root-relative storefront path with the parent origin when known.
const buildHref = (path) => (parentOrigin ? `${parentOrigin}${path}` : path);

const OrderPage = () => {
  const { orderId } = useParams();
  const { setIsLoading } = useLoading();
  const { enqueueSnackbar } = useSnackbar();

  const navigate = useNavigate();

  const [currentOrder, setCurrentOrder] = useState(null);
  const [statusesList, setStatusesList] = useState([]);
  const [trackingsList, setTrackingsList] = useState([]);
  const [productsByCategories, setProductsByCategories] = useState({});
  const [backorderedMetafield, setBackorderedMetafield] = useState(null);

  const categoryTitles = {
    backordered: 'BACKORDERED',
    cancelledRefunded: 'CANCELLED/REFUNDED',
    processing: 'PROCESSING',
    returnInitiated: 'RETURN INITIATED',
    shipped: 'SHIPPED'
  };

  const navigateBack = () => {
    window.parent.postMessage({
      'action': 'redirectBack'
    }, "*");
    // navigate('/');
  };

  const parsePrice = (price) => {
    if (price) {
      return parseFloat(price).toFixed(2);
    }

    return null;
  };

  const formatBackorderedDate = (date) => {
    if (!date) {
      return '';
    }

    // The metafield carries a date-only string ("2026-07-30"). `new Date(str)`
    // parses that as UTC midnight, which date-fns then renders in local time —
    // one day early for any US timezone. Parse the parts as a LOCAL date so the
    // rendered day matches the metafield.
    const [y, m, d] = String(date).split('T')[0].split('-').map(Number);

    return format(new Date(y, m - 1, d), 'MMMM do, yyyy');
  };

  const isPriceNotZero = (price) => {
    return parseFloat(price) !== 0;
  };

  // Raw per-unit amounts as floats, straight off the money set, parsed from the
  // RAW amount string — never from a pre-rounded output — so the fallback
  // multiplication below rounds exactly once.
  //
  // Flavor note: the receipt column uses LINE-LEVEL prices (discountedUnitPriceSet
  // nets sales on the line only). Order-level code discounts are deliberately NOT
  // baked into rows — they appear once, in the "− Discount" row (Shopify's
  // subtotalPriceSet already nets them; see the Subtotal row below). The
  // financialSummaries fallback (post-ALL-discounts, Shopify calls it
  // approximate) only kicks in if the API ever misses the line-level fields.
  const getRawUnitAmounts = (product) => {
    const originalStr =
      product?.originalUnitPriceSet?.presentmentMoney?.amount ??
      product?.financialSummaries?.at(0)?.originalUnitPriceSet?.presentmentMoney?.amount;
    const discountedStr =
      product?.discountedUnitPriceSet?.presentmentMoney?.amount ??
      product?.financialSummaries?.at(0)?.approximateDiscountedUnitPriceSet?.presentmentMoney?.amount;

    return {
      original: originalStr != null ? parseFloat(originalStr) : null,
      discounted: discountedStr != null ? parseFloat(discountedStr) : null,
    };
  };

  // Unit price for the "N × $unit" breakdown — same line-level flavor as the
  // line totals so the breakdown always multiplies out to the shown total.
  const getUnitPrice = (product) => {
    const { original, discounted } = getRawUnitAmounts(product);
    const unit = discounted != null ? discounted : original;

    return unit != null ? unit.toFixed(2) : null;
  };

  // Line totals as display strings: { original, discounted } where discounted is
  // null when it equals original (no line discount — render a single price).
  //
  // Source-of-truth rule (honors Tim's preference for Shopify's precomputed
  // totals over manual multiplication): use discountedTotalSet/originalTotalSet
  // directly when the product carries them AND the row shows exactly the
  // quantity those totals are scoped to. lineTotalsQuantity (set by the API for
  // shipped rows, where the totals come from the FulfillmentLineItem and cover
  // the fulfilled quantity) wins over the whole-line quantity that LineItem
  // totals cover (cancelledRefunded / returnInitiated). Partial slices and the
  // fulfillmentOrder buckets (processing/backordered — unit prices only) fall
  // through to the fallback, which multiplies the RAW unit amount by showQty
  // and rounds ONCE at the end.
  const getLineTotals = (product) => {
    const originalTotalStr = product?.originalTotalSet?.presentmentMoney?.amount;
    const discountedTotalStr = product?.discountedTotalSet?.presentmentMoney?.amount;
    const qty = product?.showQty || 1;
    const totalsQty = product?.lineTotalsQuantity ?? product?.quantity;
    const totalsScopeMatches = totalsQty != null && qty === totalsQty;

    if (originalTotalStr != null && discountedTotalStr != null && totalsScopeMatches) {
      const original = parseFloat(originalTotalStr);
      const discounted = parseFloat(discountedTotalStr);

      return {
        original: original.toFixed(2),
        discounted: discounted === original ? null : discounted.toFixed(2),
      };
    }

    const { original: rawOriginal, discounted: rawDiscounted } = getRawUnitAmounts(product);
    const unit = rawDiscounted != null ? rawDiscounted : rawOriginal;
    const originalTotal = (rawOriginal != null ? rawOriginal : (unit || 0)) * qty;
    const discountedTotal = (unit != null ? unit : 0) * qty;

    return {
      original: originalTotal.toFixed(2),
      discounted: discountedTotal === originalTotal ? null : discountedTotal.toFixed(2),
    };
  };

  const parseStatusCreatedDate = (date) => {
    const dateObj = new Date(date);

    return format(dateObj, 'MMMM dd, HH:mm');
  };

  const sendRedirectMessage = (url, target) => {
    window.parent.postMessage({
      'action': 'redirect',
      'target': target,
      'data': url
    }, "*");
  };

  usePostFrameHeight();

  // Per-tenant Need-help paths, resolved from the tenant config applied at boot
  // (index.js). Falls back to the PartsVu defaults baked into runtimeConfig.
  const helpLinks = getTenantLinks();

  useEffect(() => {
    if (orderId) {
      setIsLoading(true);

      orderApi.getById(orderId)
        .then(response => {
          const order = response?.data?.order;
          const statuses = response?.data?.statuses;
          const trackings = response?.data?.trackings;
          const products = response?.data?.productsByCategories;
          const metafield = response?.data?.backorderedMetafield?.node;

          if (order) {
            setCurrentOrder(order);
          }

          if (statuses) {
            setStatusesList(statuses);
          }

          if (trackings) {
            setTrackingsList(trackings);
          }

          if (products) {
            setProductsByCategories(products);
          }

          if (metafield) {
            setBackorderedMetafield(metafield);
          }
        })
        .catch(error => {
          enqueueSnackbar(error?.message || 'Something went wrong', { variant: 'error' });
          navigateBack();
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [orderId]);

  return (
    <div className="order-page">
      {currentOrder && (
        <>
          <div className="left-block">
            <div className="top-block">
              <span className="order-number">Order {currentOrder?.name}</span>
              <span className="thank-you-text">Thank you for your order! Please come back to this page for more status updates.</span>
            </div>
            <div className="statuses-block">
              {statusesList.map((status, index) => (
                <div className="status-line" key={index}>
                  <div className="left">
                    <img src={`${process.env.PUBLIC_URL}/images/checked-mark.svg`} />
                    <div className="vertical-line"></div>
                  </div>
                  <div className="right">
                    <span className="status-title">{status.title}</span>
                    <span className="status-description">{status.description}</span>
                    <span className="status-date">{parseStatusCreatedDate(status.created_at)}</span>
                  </div>
                </div>
              ))}
            </div>
            {Boolean(trackingsList.length) && (
              <div className="tracking-block">
                {trackingsList.map((tracking, key) => (
                  <div className="track" key={key}>
                    <span className="title">{tracking.company} Tracking Number</span>
                    <div className="link">
                      <a href="#" onClick={() => { sendRedirectMessage(tracking.url, '_blank') }}>{tracking.number}</a>
                      <img src={`${process.env.PUBLIC_URL}/images/arrow-forward.svg`} />
                    </div>
                  </div>
                ))}
              </div>
            )}
            <button className="custom-btn" onClick={navigateBack}>Search Orders</button>
            <div className="bottom-block">
              <img src={`${process.env.PUBLIC_URL}/images/question-mark.svg`} />
              <span className="text"><b>Need help?</b> <a href={buildHref(helpLinks.contact)} onClick={(event) => { event.preventDefault(); sendRedirectMessage(helpLinks.contact, null) }}>Contact us</a> or visit our <a href={buildHref(helpLinks.shippingPolicy)} onClick={(event) => { event.preventDefault(); sendRedirectMessage(helpLinks.shippingPolicy, null) }}>shipping and return policies</a> for more information.</span>
            </div>
          </div>
          <div className="right-block">
            <div className="top-block">
              <span className="title">Order Status Details</span>
            </div>
            <div className="products-block">
              {Object.keys(productsByCategories).map(categoryName => (
                Boolean(productsByCategories[categoryName].length) && (
                  <div className="category-block" key={categoryName}>
                    <span className="category-name">
                      {categoryTitles[categoryName] || ''}
                      {categoryName === 'returnInitiated' && (
                        <span className="category-description">{productsByCategories[categoryName].map(product => product.returnNumber).join(', ')}</span>
                      )}
                      {categoryName === 'backordered' && (
                        <span className="category-description">Expected to ship by {formatBackorderedDate(backorderedMetafield?.value)}</span>
                      )}
                    </span>
                    <div className="products-list">
                      {productsByCategories[categoryName].map((product, index) => {
                        const lineTotals = getLineTotals(product);
                        const unitPrice = getUnitPrice(product);

                        return (
                        <div className="product" key={index}>
                          <div className="image">
                            <img src={product?.image?.url} />
                            <span className="qty-circle">{product?.showQty}</span>
                          </div>
                          <div className="full-name">
                            <span className="title">{product?.title || product?.productTitle}</span>
                            <span className="variation-title">{product?.variantTitle}</span>
                            <div className="price mobile-only">
                              <span className="value">
                                {lineTotals.discounted ? (
                                  <span>
                                    <span className="crossed-price">${lineTotals.original}</span> ${lineTotals.discounted}
                                  </span>
                                ) : (
                                  <span>
                                    ${lineTotals.original}
                                  </span>
                                )}
                              </span>
                              {product?.showQty > 1 && (
                                <span className="unit-breakdown">{product?.showQty} × ${unitPrice}</span>
                              )}
                            </div>
                          </div>
                          <div className="price web-only">
                            <span className="value">
                              {lineTotals.discounted ? (
                                <span>
                                  <span className="crossed-price">${lineTotals.original}</span> ${lineTotals.discounted}
                                </span>
                              ) : (
                                <span>
                                  ${lineTotals.original}
                                </span>
                              )}
                            </span>
                            {product?.showQty > 1 && (
                              <span className="unit-breakdown">{product?.showQty} × ${unitPrice}</span>
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )
              ))}
            </div>
            <div className="hr-line"></div>
            <div className="subtotal-block">
              <div className="line">
                <span className="title">Subtotal</span>
                {/* Shopify's subtotalPriceSet already nets order-level code
                    discounts (verified empirically on DEV1334), but the rows
                    above show line-level prices and the code is subtracted in
                    its own "− Discount" row — so add the cart discount back to
                    keep Σ(rows) == Subtotal and the column penny-exact. */}
                <span className="value">${(parseFloat(currentOrder?.subtotalPriceSet?.presentmentMoney?.amount || 0) + parseFloat(currentOrder?.cartDiscountAmountSet?.presentmentMoney?.amount || 0)).toFixed(2)}</span>
              </div>
              <div className="line">
                <span className="title">Shipping</span>
                <span className="value">${parsePrice(currentOrder?.totalShippingPriceSet?.presentmentMoney?.amount) || '0.00'}</span>
              </div>
              {currentOrder?.cartDiscountAmountSet && isPriceNotZero(currentOrder?.cartDiscountAmountSet?.presentmentMoney?.amount) && (
                <div className="line">
                  <span className="title">Discount</span>
                  <span className="value">- ${parsePrice(currentOrder?.cartDiscountAmountSet?.presentmentMoney?.amount)}</span>
                </div>
              )}
              <div className="line">
                <span className="title">Tax</span>
                <span className="value">${parsePrice(currentOrder?.totalTaxSet?.presentmentMoney?.amount)}</span>
              </div>
              {isPriceNotZero(currentOrder?.totalRefundedSet?.presentmentMoney?.amount) && (
                <div className="line">
                  <span className="title">Refund Amount</span>
                  <span className="value">- ${parsePrice(currentOrder?.totalRefundedSet?.presentmentMoney?.amount)}</span>
                </div>
              )}
            </div>
            <div className="hr-line"></div>
            <div className="total-block">
              <div className="line">
                <span className="title">Total</span>
                <span className="value">${parseFloat(parseFloat(currentOrder?.totalPriceSet?.presentmentMoney?.amount) - parseFloat(currentOrder?.totalRefundedSet?.presentmentMoney?.amount)).toFixed(2)}</span>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default OrderPage;
